import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../lib/firebase';

export default function AdminRinnovo() {
  const { societaId }               = useParams();
  const [societa, setSocieta]       = useState(null);
  const [iscritti, setIscritti]     = useState([]);
  const [utenti, setUtenti]         = useState({});
  const [selezionati, setSelezionati] = useState({});
  const [caricamento, setCaricamento] = useState(true);
  const [elaborazione, setElaborazione] = useState(false);
  const [nuovoAnno, setNuovoAnno]   = useState('2026-2027');
  const [scadenza, setScadenza]     = useState('');

  const functions = getFunctions(undefined, 'europe-west1');

  useEffect(() => {
    if (!societaId) return;
    Promise.all([
      getDoc(doc(db, 'societa', societaId)),
      getDocs(collection(db, 'societa', societaId, 'iscrizioni')),
    ]).then(async ([societaSnap, iscrizioniSnap]) => {
      if (societaSnap.exists()) setSocieta(societaSnap.data());

      const lista = iscrizioniSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(i => i.statoAccount === 'attivo');

      setIscritti(lista);

      // Pre-seleziona tutti
      const sel = {};
      lista.forEach(i => sel[i.id] = true);
      setSelezionati(sel);

      // Carica anagrafica
      const uMap = {};
      await Promise.all(lista.map(async i => {
        const snap = await getDoc(doc(db, 'utenti', i.id));
        if (snap.exists()) uMap[i.id] = snap.data();
      }));
      setUtenti(uMap);
      setCaricamento(false);
    });
  }, [societaId]);

  function toggleTutti() {
    const tutti = iscritti.every(i => selezionati[i.id]);
    const sel = {};
    if (!tutti) iscritti.forEach(i => sel[i.id] = true);
    setSelezionati(sel);
  }

  async function apriRinnovo() {
    if (!scadenza) { alert('Inserisci la data di scadenza'); return; }
    if (!window.confirm(`Aprire il rinnovo per la stagione ${nuovoAnno}?\n\nTutti gli iscritti attivi passeranno allo stato "da rinnovare".`)) return;

    setElaborazione(true);
    try {
      const fn = httpsCallable(functions, 'apriRinnovo');
      const result = await fn({ societaId, scadenzaRinnovo: scadenza, nuovoAnno });
      alert(`Rinnovo aperto. ${result.data.totale} iscritti da rinnovare.`);
    } catch (e) {
      alert('Errore: ' + e.message);
    } finally {
      setElaborazione(false);
    }
  }

  async function confermaRinnovi() {
    const uids = Object.entries(selezionati)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (uids.length === 0) { alert('Nessun iscritto selezionato'); return; }
    if (!window.confirm(`Rinnovare ${uids.length} iscrizioni per la stagione ${nuovoAnno}?`)) return;

    setElaborazione(true);
    try {
      const fn = httpsCallable(functions, 'confermaRinnovi');
      const result = await fn({ societaId, uids, nuovoAnno });
      alert(`${result.data.rinnovati} iscrizioni rinnovate.`);
    } catch (e) {
      alert('Errore: ' + e.message);
    } finally {
      setElaborazione(false);
    }
  }

  async function chiudiRinnovo() {
    if (!window.confirm('Chiudere il rinnovo? Chi non ha rinnovato decadrà.')) return;
    setElaborazione(true);
    try {
      const fn = httpsCallable(functions, 'chiudiRinnovo');
      const result = await fn({ societaId, nuovoAnno });
      alert(`Rinnovo chiuso. ${result.data.decaduti} iscrizioni decadute.`);
    } catch (e) {
      alert('Errore: ' + e.message);
    } finally {
      setElaborazione(false);
    }
  }

  const tuttiSelezionati = iscritti.length > 0 && iscritti.every(i => selezionati[i.id]);

  return (
    <div className="">
      <h2 className="text-lg font-bold text-gray-900 mb-5">Rinnovo annuale</h2>

      {/* Configurazione */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nuova stagione
          </label>
          <input type="text" value={nuovoAnno}
            onChange={e => setNuovoAnno(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200
                       focus:outline-none focus:ring-2 focus:ring-scherma-blue text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Scadenza rinnovo
          </label>
          <input type="date" value={scadenza}
            onChange={e => setScadenza(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200
                       focus:outline-none focus:ring-2 focus:ring-scherma-blue text-sm"
          />
        </div>

        <button onClick={apriRinnovo} disabled={elaborazione}
          className="w-full bg-yellow-500 text-white py-3 rounded-xl
                     font-medium text-sm disabled:opacity-50">
          Apri periodo di rinnovo
        </button>
      </div>

      {/* Lista iscritti con checkbox */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4">
        <div className="flex items-center justify-between px-4 py-3
                        border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">
            Iscritti da rinnovare ({iscritti.length})
          </p>
          <button onClick={toggleTutti}
            className="text-xs text-scherma-blue font-medium">
            {tuttiSelezionati ? 'Deseleziona tutti' : 'Seleziona tutti'}
          </button>
        </div>

        {caricamento ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-4 border-scherma-blue border-t-transparent
                            rounded-full animate-spin" />
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {iscritti.map(iscr => {
              const u = utenti[iscr.id] || {};
              return (
                <label key={iscr.id}
                       className="flex items-center gap-3 px-4 py-3 cursor-pointer
                                  hover:bg-gray-50">
                  <input type="checkbox"
                    checked={!!selezionati[iscr.id]}
                    onChange={e => setSelezionati(prev => ({
                      ...prev, [iscr.id]: e.target.checked
                    }))}
                    className="w-5 h-5 rounded border-gray-300 text-scherma-blue"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {u.nome} {u.cognome}
                    </p>
                    <p className="text-xs text-gray-400">
                      {iscr.ruoli?.join(', ')}
                      {iscr.categoriaCalcolata && ` · ${iscr.categoriaCalcolata}`}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <button onClick={confermaRinnovi} disabled={elaborazione}
          className="w-full bg-scherma-blue text-white py-4 rounded-2xl
                     font-semibold text-sm disabled:opacity-50">
          {elaborazione ? 'Elaborazione...' : 'Conferma rinnovi selezionati'}
        </button>
        <button onClick={chiudiRinnovo} disabled={elaborazione}
          className="w-full border border-red-200 text-red-500 py-3 rounded-2xl
                     font-medium text-sm disabled:opacity-50">
          Chiudi rinnovo (fa decadere i non rinnovati)
        </button>
      </div>
    </div>
  );
}
